import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, CheckCircle2, Truck, Package, XCircle, Calendar, Plus, Minus, Edit3, Lock, ShieldCheck, ClipboardCheck, AlertTriangle } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useOrderTracking } from "../../lib/liveGps";
import { formatINR, formatDate } from "../../lib/utils.fmt";
import { toast } from "sonner";
import LoginRequired from "./LoginRequired";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import Pagination from "../../components/Pagination";

const PAGE_SIZE = 20;

const STATUS_MAP = {
  placed: { color: "bg-brand-sand/15 text-brand-sand", Icon: ClipboardCheck, label: "Placed" },
  confirmed: { color: "bg-brand-sky/10 text-brand-sky", Icon: ShieldCheck, label: "Confirmed" },
  pending: { color: "bg-neutral-200 text-neutral-700", Icon: Calendar, label: "Pending" },
  out_for_delivery: { color: "bg-brand-sky/10 text-brand-sky", Icon: Truck, label: "Out for delivery" },
  delivered: { color: "bg-brand-leaf/10 text-brand-leaf", Icon: CheckCircle2, label: "Delivered" },
  failed: { color: "bg-brand-clay/10 text-brand-clay", Icon: XCircle, label: "Failed" },
  skipped: { color: "bg-neutral-100 text-neutral-500", Icon: XCircle, label: "Skipped" },
  cancelled: { color: "bg-brand-clay/10 text-brand-clay", Icon: XCircle, label: "Cancelled" },
};

export function Orders() {
  const { user, tenant } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [busy, setBusy] = useState(null);   // delivery id being saved

  const loadUpcoming = async () => {
    try {
      const { data } = await api.get("/api/customer/deliveries/upcoming");
      setUpcoming(data.deliveries || []);
    } catch { /* ignore */ }
  };

  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const loadDeliveries = (targetPage = page) => {
    setLoadError(null);
    return api.get(`/api/customer/deliveries?page=${targetPage}&page_size=${PAGE_SIZE}`)
      .then(({ data }) => {
        setDeliveries(data.deliveries);
        setTotal(data.total);
        setTotalPages(data.total_pages);
      })
      .catch((e) => setLoadError(friendlyMessage(e)));
  };
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    loadDeliveries(1).finally(() => setLoading(false));
    loadUpcoming();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const goToPage = (p) => {
    setPage(p);
    setPageLoading(true);
    loadDeliveries(p).finally(() => setPageLoading(false));
  };

  const updateQty = async (id, qty) => {
    setBusy(id);
    try {
      await api.patch(`/api/customer/deliveries/${id}/quantity`, { quantity: qty });
      toast.success(qty === 0 ? "Skipped for that day" : `Updated to ${qty} qty`);
      await loadUpcoming();
      await loadDeliveries();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const cancelOrder = async (id) => {
    if (!window.confirm("Cancel this order? You won't be able to undo this.")) return;
    setBusy(id);
    try {
      await api.post(`/api/customer/orders/${id}/cancel`);
      toast.success("Order cancelled");
      await loadUpcoming();
      await loadDeliveries();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
    finally { setBusy(null); }
  };

  if (!user) return <LoginRequired title="Sign in to view orders" description="See past deliveries, bottle returns and track ongoing orders." />;
  if (loading) return <LoadingState fullHeight />;
  if (loadError) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load your orders" description={loadError}
        action={<button onClick={() => { setLoading(true); loadDeliveries().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }

  const primary = tenant?.branding?.primary_color || "#3A86FF";
  return (
    <div className="page-enter pb-24 min-h-screen flex flex-col" data-testid="orders-page">
      <header className="sticky top-0 z-30 bg-brand-cream/95 backdrop-blur pt-safe">
        <div className="px-5 pt-5 pb-4 flex items-center gap-2">
          <Link to="/app/profile" className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">My Orders</h1>
        </div>
      </header>

      <main className="px-5 space-y-6 flex-1 flex flex-col">
        {/* Upcoming · Editable */}
        {upcoming.length > 0 && (
          <section data-testid="upcoming-section">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading text-lg font-extrabold tracking-tight flex items-center gap-1.5">
                <Edit3 className="w-4 h-4" style={{ color: primary }}/> Upcoming
              </h2>
              {upcoming.some((d) => d.order_type !== "one_time" && d.editable) && (
                <span className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider" data-testid="qty-hint">
                  Tap ± to change qty
                </span>
              )}
            </div>
            <div className="space-y-2">
              {upcoming.map((d) => {
                const isLocked = !d.editable;
                // The backend says WHY a row is locked (`lock_reason`).
                // Only "cutoff" is an actual deadline; "status" means the
                // delivery has moved on (out for delivery, delivered,
                // cancelled). Calling both "Cutoff passed" told customers a
                // deadline had expired when none had. Older backends don't
                // send lock_reason — fall back to the previous wording.
                const lockLabel = d.lock_reason === "status" ? "Locked" : "Cutoff passed";
                const isSkipped = d.status === "skipped";
                const isOneTime = d.order_type === "one_time";
                const status = STATUS_MAP[d.status] || STATUS_MAP.pending;
                return (
                  <div key={d.id} className={`bg-white rounded-2xl border p-3 flex items-center gap-3 ${isLocked ? "border-neutral-200 opacity-70" : isSkipped ? "border-neutral-300" : "border-neutral-200"}`}
                       data-testid={`upcoming-${d.id}`}>
                    <div className="w-11 h-11 rounded-2xl bg-brand-cream flex flex-col items-center justify-center shrink-0">
                      <div className="text-[9px] uppercase tracking-wider font-bold text-neutral-500">
                        {new Date(d.delivery_date).toLocaleDateString("en-IN", { weekday: "short" })}
                      </div>
                      <div className="font-heading font-extrabold text-sm leading-none">
                        {new Date(d.delivery_date).getDate()}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-sm truncate ${isSkipped ? "line-through text-neutral-400" : ""}`}>
                        {d.product_name}
                      </div>
                      <div className="text-xs text-neutral-500 capitalize flex items-center gap-1.5 flex-wrap">
                        <span>{d.slot}</span>
                        {isOneTime && (
                          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider ${status.color}`}
                                data-testid={`upcoming-status-${d.id}`}>
                            {status.label}
                          </span>
                        )}
                      </div>
                    </div>

                    {isOneTime ? (
                      d.cancellable ? (
                        <button onClick={() => cancelOrder(d.id)}
                                disabled={busy === d.id}
                                className="px-3 h-8 rounded-full bg-brand-clay/10 text-brand-clay font-bold text-xs disabled:opacity-50"
                                data-testid={`upcoming-cancel-${d.id}`}>
                          Cancel
                        </button>
                      ) : (
                        <div className="text-xs font-bold text-neutral-500 inline-flex items-center gap-1" data-testid={`upcoming-locked-${d.id}`}>
                          <Lock className="w-3.5 h-3.5"/> {lockLabel}
                        </div>
                      )
                    ) : isLocked ? (
                      <div className="text-xs font-bold text-neutral-500 inline-flex items-center gap-1" data-testid={`locked-${d.id}`}>
                        <Lock className="w-3.5 h-3.5"/> {lockLabel}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button onClick={() => updateQty(d.id, Math.max(0, d.quantity - 1))}
                                disabled={busy === d.id || d.quantity === 0}
                                className="w-8 h-8 rounded-full bg-brand-cream border border-neutral-200 inline-flex items-center justify-center disabled:opacity-40"
                                data-testid={`upcoming-minus-${d.id}`}>
                          <Minus className="w-3.5 h-3.5"/>
                        </button>
                        <span className={`font-mono font-extrabold w-6 text-center text-sm ${isSkipped ? "text-neutral-400" : ""}`}
                              data-testid={`upcoming-qty-${d.id}`}>
                          {isSkipped ? "0" : d.quantity}
                        </span>
                        <button onClick={() => updateQty(d.id, (isSkipped ? 0 : d.quantity) + 1)}
                                disabled={busy === d.id || (d.quantity >= 50)}
                                className="w-8 h-8 rounded-full text-white inline-flex items-center justify-center disabled:opacity-40"
                                style={{ background: primary }}
                                data-testid={`upcoming-plus-${d.id}`}>
                          <Plus className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Only explain the cutoff when a row is genuinely blocked by
                  it. This note used to render next to any locked row, so a
                  list locked for unrelated reasons still read as a cutoff
                  problem. */}
              {upcoming.some((d) => d.lock_reason === "cutoff") && (
                // Each delivery locks at its OWN slot's cutoff (a morning
                // delivery can be locked while the evening one the same day is
                // still open), so no single "from this date" can be named.
                <div className="text-[11px] text-neutral-500 px-1 pt-1" data-testid="cutoff-note">
                  ⓘ A delivery can't be changed once its slot's order cutoff has passed.
                </div>
              )}
            </div>
          </section>
        )}

        {/* Delivery History — excludes anything already shown in the Upcoming
            section above (same delivery id) so the customer doesn't see a
            single order duplicated in two places. */}
        <section className="flex-1 flex flex-col">
          <h2 className="font-heading text-lg font-extrabold tracking-tight mb-2">Delivery History</h2>
          <div className="space-y-3 flex-1 flex flex-col">
            {(() => {
              const upcomingIds = new Set(upcoming.map((u) => u.id));
              const history = deliveries.filter((d) => !upcomingIds.has(d.id));
              if (history.length === 0) return (
                <EmptyState
                  icon={Package}
                  title="No past deliveries yet"
                  description="Your delivery history will show up here once your first order is completed."
                  fullHeight
                  testId="orders-history-empty"
                />
              );
              return history.map((d) => {
              const s = STATUS_MAP[d.status] || STATUS_MAP.pending;
              return (
                <Link key={d.id} to={`/app/orders/${d.id}`}
                      className="bg-white rounded-3xl border border-neutral-200 p-4 flex items-center gap-3 card-hover"
                      data-testid={`order-${d.id}`}>
                  <div className={`w-12 h-12 rounded-2xl ${s.color} flex items-center justify-center`}>
                    <s.Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{d.product_name} × {d.quantity}</div>
                    <div className="text-xs text-neutral-500 flex items-center gap-1.5 flex-wrap">
                      <span>{formatDate(d.delivery_date)} · {d.slot}</span>
                      {d.order_type === "one_time" && (
                        <span className="px-1.5 py-0.5 rounded-full bg-brand-sand/15 text-brand-sand text-[10px] font-extrabold uppercase tracking-wider" data-testid="one-time-badge">One-time</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-bold">{formatINR(d.amount)}</div>
                    <div className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${s.color} mt-1 inline-block`}>{s.label}</div>
                  </div>
                </Link>
              );
              });
            })()}
          </div>
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE}
                      onPageChange={goToPage} loading={pageLoading} testId="orders-history-pagination" />
        </section>
      </main>
    </div>
  );
}

export function OrderTrack() {
  const { id } = useParams();
  const { user, tenant } = useAuth();
  const [d, setD] = useState(null);
  const [boy, setBoy] = useState(null);
  const [trackError, setTrackError] = useState(null);
  const bottlesOn = tenant?.bottle_tracking_enabled !== false;
  // Live WS rider position (overrides slow HTTP poll for lat/lng).
  const { position: livePos, status: liveStatus } = useOrderTracking(id);

  const fetchTrack = () => api.get(`/api/customer/deliveries/${id}/track`).then(({ data }) => {
    setD(data.delivery); setBoy(data.delivery_boy); setTrackError(null);
  }).catch((e) => {
    // Only surface an error screen if we've never successfully loaded —
    // once tracking is live, a single dropped poll (network blip) shouldn't
    // yank the page out from under the customer; the next 20s tick retries.
    setD((cur) => { if (!cur) setTrackError(friendlyMessage(e)); return cur; });
  });

  useEffect(() => {
    if (!user) return;
    fetchTrack();
    // Slower fallback poll: WS handles the high-frequency updates; HTTP only
    // refreshes the surrounding delivery status / rider profile.
    const i = setInterval(fetchTrack, 20000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  if (!user) return <LoginRequired title="Sign in to track this delivery" />;
  if (trackError && !d) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load tracking" description={trackError}
        action={<button onClick={fetchTrack} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }
  if (!d) return <LoadingState fullHeight />;
  const s = STATUS_MAP[d.status] || STATUS_MAP.pending;

  return (
    <div className="page-enter" data-testid="order-track-page">
      <header className="sticky top-0 z-30 bg-brand-cream/95 backdrop-blur pt-safe">
        <div className="px-5 pt-5 pb-4 flex items-center gap-2">
          <Link to="/app/orders" className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Track Delivery</h1>
        </div>
      </header>
      <main className="px-5 space-y-4">
        <div className="bg-white rounded-3xl border border-neutral-200 p-5">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${s.color} text-sm font-bold mb-3`}>
            <s.Icon className="w-4 h-4" /> {s.label}
          </div>
          <div className="font-heading text-xl font-bold">{d.product_name} × {d.quantity}</div>
          <div className="text-sm text-neutral-500 mt-1">{formatDate(d.delivery_date)} · {d.slot} slot</div>
          <div className="mt-3 text-sm text-neutral-700">{d.address?.line1}, {d.address?.city} - {d.address?.pincode}</div>
        </div>

        {boy && (
          <div className="bg-brand-sky/5 border border-brand-sky/30 rounded-3xl p-5" data-testid="rider-card">
            <div className="text-xs uppercase tracking-wider font-bold text-brand-sky mb-2">Your Rider</div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-brand-sky text-white flex items-center justify-center font-heading font-extrabold text-lg">
                {boy.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate" data-testid="rider-name">{boy.name}</div>
                {boy.phone && <div className="text-sm text-neutral-600 font-mono" data-testid="rider-phone">{boy.phone}</div>}
              </div>
              {boy.phone && (
                <a href={`tel:${boy.phone}`} className="px-4 h-10 rounded-2xl bg-brand-sky text-white font-bold text-sm inline-flex items-center" data-testid="call-rider">Call</a>
              )}
            </div>
            {d.status === "out_for_delivery" && (
              <>
                <div className="mt-3 text-sm text-neutral-700 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${liveStatus === "live" ? "bg-brand-leaf" : "bg-neutral-300"} animate-pulse`} data-testid="live-indicator" />
                  {liveStatus === "live" ? "Live tracking · rider is moving" : "Rider is approaching · ETA ~12 min"}
                </div>
                <div className="mt-3 text-xs text-neutral-500" data-testid="rider-coords">
                  {(() => {
                    const lat = livePos?.lat ?? boy.current_lat;
                    const lng = livePos?.lng ?? boy.current_lng;
                    if (lat == null || lng == null) return "Waiting for rider location…";
                    const fresh = liveStatus === "live" ? "live" : "auto-refreshes";
                    return `Lat ${lat.toFixed(4)}, Lng ${lng.toFixed(4)} · ${fresh}`;
                  })()}
                </div>
              </>
            )}
          </div>
        )}

        <div className="bg-white rounded-3xl border border-neutral-200 p-5">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-3">Delivery Summary</div>
          <div className="flex justify-between text-sm py-1.5"><span className="text-neutral-600">Amount</span><span className="font-mono font-bold">{formatINR(d.amount)}</span></div>
          <div className="flex justify-between text-sm py-1.5"><span className="text-neutral-600">Slot</span><span className="font-bold capitalize">{d.slot}</span></div>
          <div className="flex justify-between text-sm py-1.5"><span className="text-neutral-600">Status</span><span className="font-bold">{s.label}</span></div>
          {bottlesOn && d.bottles_returned_count > 0 && (
            <div className="flex justify-between text-sm py-1.5"><span className="text-neutral-600">Bottles returned</span><span className="font-bold">{d.bottles_returned_count}</span></div>
          )}
        </div>
      </main>
    </div>
  );
}
